import { useNavigate, useParams } from 'react-router-dom';
import { KeyRound, ListChecks, ShieldCheck } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { useI18n } from '@/i18n';
import { AssetOverviewTab } from '@/features/assets/components/AssetOverviewTab';
import { AssetListTab } from '@/features/assets/components/AssetListTab';

type TabType = 'overview' | 'list';

export function AssetsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const currentTab: TabType = tab === 'list' ? 'list' : 'overview';

  const tabs: { id: TabType; label: string; icon: typeof ShieldCheck }[] = [
    { id: 'overview', label: t('assetsPage.tabs.overview'), icon: ShieldCheck },
    { id: 'list', label: t('assetsPage.tabs.list'), icon: ListChecks },
  ];

  const handleTabChange = (next: TabType) =>
    navigate(next === 'overview' ? '/assets' : `/assets/${next}`);

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <KeyRound className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('assetsPage.pageTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('assetsPage.pageDescription')}</p>
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={(value) => handleTabChange(value as TabType)} className="mb-6">
        <TabsList>
          {tabs.map(({ id, label, icon: Icon }) => (
            <TabsTrigger
              key={id}
              value={id}
              className="gap-2"
            >
              <Icon className="h-4 w-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {currentTab === 'overview' && <AssetOverviewTab />}
      {currentTab === 'list' && <AssetListTab />}
    </div>
  );
}
